import * as pc from 'playcanvas';
import { SensorLook } from './sensor-look.js';
import { createStagedScene, hasStaging, pick } from './staging.js';

const MODE_BROWSER = 'browser';
const MODE_SPATIAL = 'mobile_spatial';
const MODE_HEADSET = 'phone_headset';
const MODE_XR = 'vr';
const BOARD_TILT_X = 90;
const BOARD_SPIN_Y = 180;
const GAZE_SECONDS = 1.6;
const XR_RAY_COLOR = new pc.Color(0.85, 0.9, 1, 1);
const XR_RAY_HOT = new pc.Color(1, 0.78, 0.3, 1);

function color(value, fallback = '#ffffff') {
  const source = String(value || fallback).replace('#', '');
  const parsed = Number.parseInt(source.length === 3 ? source.split('').map(item => item + item).join('') : source, 16);
  return new pc.Color(((parsed >> 16) & 255) / 255, ((parsed >> 8) & 255) / 255, (parsed & 255) / 255);
}

function material(diffuse, { emissive = null, opacity = 1, unlit = false } = {}) {
  const result = new pc.StandardMaterial();
  result.diffuse = color(diffuse);
  result.opacity = opacity;
  result.blendType = opacity < 1 ? pc.BLEND_NORMAL : pc.BLEND_NONE;
  result.useLighting = !unlit;
  if (emissive) result.emissive = color(emissive);
  result.update();
  return result;
}

function primitive(name, type, mat, scale, position = [0, 0, 0]) {
  const entity = new pc.Entity(name);
  entity.addComponent('render', { type, material: mat, castShadows: true, receiveShadows: true });
  entity.setLocalScale(scale[0], scale[1], scale[2]);
  entity.setLocalPosition(position[0], position[1], position[2]);
  return entity;
}

function readablePosition(value, fallback) {
  const candidate = Array.isArray(value) ? value : fallback;
  return new pc.Vec3(Number(candidate[0] || 0), Number(candidate[1] || 0), Number(candidate[2] || 0));
}

export class ExperienceEngine {
  constructor(canvas, callbacks = {}) {
    this.canvas = canvas;
    this.callbacks = callbacks;
    this.sensor = new SensorLook();
    this.mode = MODE_BROWSER;
    this.app = null;
    this.cameraRig = null;
    this.camera = null;
    this.stereoLeft = null;
    this.stereoRight = null;
    this.characters = new Map();
    this.bystanders = [];
    this.choiceBoards = [];
    this.focusKey = '';
    this.lookYaw = 0;
    this.lookPitch = -4;
    this.drag = null;
    this.elapsed = 0;
    this.mouthLevel = 0;
    this.sceneDefinition = null;
    this.xrInputAttached = new WeakSet();
    this.performanceVideo = null;
    this.performanceTexture = null;
    this.performanceMaterial = null;
    this.performanceEntity = null;
    // 2026-10-09h: staged 3D scene (approved 3D bodies + per-moment staging) instead of the video clips
    this.stagedMode = false;
    this.staged = null;
    this.speakingKey = '';
    this.manualLook = false;
  }

  // The scenario opts in (useBodies), has staging, and at least one cast member has an approved body.
  static wantsStaged(definition) {
    const characters = definition?.characters || [];
    return Boolean(pick(definition, 'useBodies')) && hasStaging(definition) && characters.some(c => pick(c, 'modelURL'));
  }

  async initialize(definition) {
    this.sceneDefinition = definition;
    this.app = new pc.Application(this.canvas, {
      mouse: new pc.Mouse(this.canvas),
      touch: new pc.TouchDevice(this.canvas),
      keyboard: new pc.Keyboard(window),
      graphicsDeviceOptions: {
        antialias: true,
        alpha: false,
        preserveDrawingBuffer: false,
        powerPreference: 'high-performance'
      }
    });
    this.app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
    this.app.setCanvasResolution(pc.RESOLUTION_AUTO);
    // The engine draws one pixel per screen point by default; phones have 2-3, so text and faces were soft
    // (and split-screen halves it again). Draw at the phone's real sharpness, capped at 2x for smoothness.
    this.app.graphicsDevice.maxPixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    this.app.scene.ambientLight = new pc.Color(0.42, 0.49, 0.62);
    this.app.scene.toneMapping = pc.TONEMAP_ACES;
    this.app.scene.exposure = 1.15;
    this.app.start();
    this.createCamera();
    this.stagedMode = ExperienceEngine.wantsStaged(definition);
    if (this.stagedMode) {
      await this.createStagedScene(definition);
    } else {
      this.createLighting();
      await this.createEnvironment(definition.location || {});
      await this.createCharacters(definition.characters || []);
      this.createBystanders();
    }
    this.bindControls();
    this.app.on('update', delta => this.update(delta));
    this.app.xr.input.on('add', inputSource => this.attachXrInput(inputSource));
    window.addEventListener('resize', () => this.applyCanvasSize());
  }

  createCamera() {
    this.cameraRig = new pc.Entity('camera-rig');
    this.cameraRig.setPosition(0, 1.66, 3.7);
    this.camera = new pc.Entity('camera');
    this.camera.addComponent('camera', {
      clearColor: new pc.Color(0.025, 0.04, 0.075),
      nearClip: 0.04,
      farClip: 80,
      fov: 68
    });
    this.camera.addComponent('audiolistener');
    this.cameraRig.addChild(this.camera);
    this.app.root.addChild(this.cameraRig);
  }

  async createStagedScene(definition) {
    // Same look as the admin Scene preview: plain tone mapping so the store picture shows as photographed.
    this.app.scene.toneMapping = pc.TONEMAP_LINEAR;
    this.app.scene.exposure = 1;
    // poses, props, crowd types and events come with the scene (definition.engine, from the shared library)
    this.staged = createStagedScene(pc, this.app, { onStatus: text => this.callbacks.status?.(text), engine: pick(definition, 'engine') || {} });
    this.staged.addLights();
    await this.staged.loadStore(definition.location || {});
    // where the player stands comes from the location (loadStore)
    this.cameraRig.setPosition(this.staged.YOU.x, this.staged.EYE, this.staged.YOU.z);
    this.camera.camera.fov = 55;
    this.staged.setCamera(this.camera, this.canvas);
    const people = (definition.characters || []).map(c => ({ key: c.key, name: c.name, kind: 'cast', url: pick(c, 'modelURL') }))
      .concat((pick(definition, 'extras') || []).map(c => ({ key: c.key, name: c.name, kind: 'extra', url: pick(c, 'modelURL'), profile: pick(c, 'profile') || '' })));
    const result = await this.staged.loadPeople(people, definition.characters || []);
    if (result.missing.length) this.callbacks.assetError?.('character_model', result.missing.join(', '), new Error('Some 3D bodies did not load.'));
    const opening = definition.nodes?.[pick(definition, 'openingNode')];
    if (opening) { this.staged.stage(opening); this.staged.settle(); }
  }

  createLighting() {
    const key = new pc.Entity('sunset-key');
    key.addComponent('light', {
      type: 'directional',
      color: new pc.Color(1, 0.78, 0.6),
      intensity: 2.1,
      castShadows: true,
      shadowResolution: 1024
    });
    key.setEulerAngles(42, -34, 0);
    this.app.root.addChild(key);
    const rim = new pc.Entity('street-rim');
    rim.addComponent('light', { type: 'omni', color: new pc.Color(0.4, 0.58, 1), intensity: 1.3, range: 15 });
    rim.setPosition(2, 4, -4);
    this.app.root.addChild(rim);
  }

  createGround() {
    const ground = primitive('ground', 'plane', material('#18202b'), [20, 1, 20]);
    ground.setPosition(0, -0.03, -4);
    this.app.root.addChild(ground);
  }

  async createEnvironment(location) {
    const url = location.background360 || location.background360url || location.backgroundimage || location.backgroundImage;
    if (!url) return;
    await new Promise(resolve => {
      this.app.assets.loadFromUrl(url, 'texture', (error, asset) => {
        if (error || !asset?.resource) {
          this.callbacks.assetError?.('environment', url, error || new Error('The environment texture did not load.'));
          resolve();
          return;
        }
        asset.resource.addressU = pc.ADDRESS_REPEAT;
        asset.resource.addressV = pc.ADDRESS_CLAMP_TO_EDGE;
        const panoramaMaterial = new pc.StandardMaterial();
        // Dimmed: the panorama is only the surround for the curved performance screen.
        panoramaMaterial.emissive = new pc.Color(0.34, 0.35, 0.4);
        panoramaMaterial.emissiveMap = asset.resource;
        panoramaMaterial.diffuse = pc.Color.BLACK;
        panoramaMaterial.useLighting = false;
        panoramaMaterial.cull = pc.CULLFACE_FRONT;
        panoramaMaterial.update();
        const dome = primitive('panorama', 'sphere', panoramaMaterial, [38, 38, 38]);
        dome.setPosition(0, 0, -2);
        dome.setEulerAngles(0, Number(location.rotation ?? 180), 0);
        this.app.root.addChild(dome);
        resolve();
      });
    });
  }

  async createCharacters(definitions) {
    const tasks = definitions.map(definition => this.createCharacter(definition));
    await Promise.all(tasks);
  }

  async createCharacter(definition) {
    const root = new pc.Entity(`character-${definition.key}`);
    const start = readablePosition(definition.position, [0, 0, -4]);
    root.setPosition(start);
    root.lookAt(0, 1.45, 3.5);
    root.rotateLocal(0, 180, 0);
    root._experienceTarget = start.clone();
    root._experienceDefinition = definition;
    root._experienceAction = 'idle';
    root._experiencePhase = Math.random() * Math.PI * 2;
    this.app.root.addChild(root);
    const record = { root, definition, renderMode: 'procedural_3d', animationNames: [], mouthTargets: [], mouth: null };
    this.characters.set(definition.key, record);
    const modelUrl = definition.modelurl || definition.modelURL || definition.model;
    if (modelUrl) {
      const loaded = await this.loadGlbCharacter(record, modelUrl);
      if (loaded) return record;
      this.callbacks.assetError?.('character_model', modelUrl, new Error(`${definition.name || definition.key} could not load.`));
    }
    this.createProceduralCharacter(record);
    return record;
  }

  async loadGlbCharacter(record, url) {
    return new Promise(resolve => {
      this.app.assets.loadFromUrl(url, 'container', (error, asset) => {
        if (error || !asset?.resource) {
          resolve(false);
          return;
        }
        const model = asset.resource.instantiateRenderEntity({ castShadows: true, receiveShadows: true });
        const scale = Number(record.definition.modelscale || record.definition.scale || 1);
        model.setLocalScale(scale, scale, scale);
        record.root.addChild(model);
        record.model = model;
        record.renderMode = 'glb';
        const animations = asset.resource.animations || [];
        if (animations.length) {
          model.addComponent('animation', {
            assets: animations.map(item => item.id),
            activate: true,
            loop: true,
            speed: 1
          });
          record.animationNames = animations.map(item => item.name || item.resource?.name).filter(Boolean);
        }
        this.discoverMorphTargets(record);
        this.playAction(record, 'idle');
        resolve(true);
      });
    });
  }

  discoverMorphTargets(record) {
    const mouthNames = /jawopen|viseme|mouthopen|mouth_open/i;
    const renders = record.model?.findComponents('render') || [];
    for (const render of renders) {
      for (const instance of render.meshInstances || []) {
        const morphInstance = instance.morphInstance;
        const targets = instance.mesh?.morph?._targets || [];
        targets.forEach((target, index) => {
          if (mouthNames.test(target.name || '')) record.mouthTargets.push({ morphInstance, index });
        });
      }
    }
  }

  createProceduralCharacter(record, lowDetail = false) {
    const skin = material(record.definition.skincolor || '#8b5d43');
    const clothing = material(record.definition.color || '#344d72');
    const dark = material('#15181e');
    const root = record.root;
    const body = primitive('torso', 'capsule', clothing, lowDetail ? [0.62, 0.88, 0.42] : [0.7, 0.98, 0.46], [0, 1.22, 0]);
    const head = primitive('head', 'sphere', skin, [0.47, 0.56, 0.45], [0, 2.18, 0]);
    const hair = primitive('hair', 'sphere', dark, [0.49, 0.24, 0.47], [0, 2.43, -0.015]);
    const mouth = primitive('mouth', 'box', material('#4b1519', { emissive: '#210509', unlit: true }), [0.18, 0.025, 0.025], [0, 2.09, -0.43]);
    const leftArmPivot = new pc.Entity('left-arm-pivot');
    const rightArmPivot = new pc.Entity('right-arm-pivot');
    leftArmPivot.setLocalPosition(-0.48, 1.68, 0);
    rightArmPivot.setLocalPosition(0.48, 1.68, 0);
    const leftArm = primitive('left-arm', 'capsule', skin, [0.22, 0.72, 0.22], [0, -0.46, 0]);
    const rightArm = primitive('right-arm', 'capsule', skin, [0.22, 0.72, 0.22], [0, -0.46, 0]);
    leftArmPivot.addChild(leftArm);
    rightArmPivot.addChild(rightArm);
    const leftLeg = primitive('left-leg', 'capsule', dark, [0.29, 0.86, 0.3], [-0.22, 0.42, 0]);
    const rightLeg = primitive('right-leg', 'capsule', dark, [0.29, 0.86, 0.3], [0.22, 0.42, 0]);
    root.addChild(body);
    root.addChild(head);
    root.addChild(hair);
    root.addChild(mouth);
    root.addChild(leftArmPivot);
    root.addChild(rightArmPivot);
    root.addChild(leftLeg);
    root.addChild(rightLeg);
    record.parts = { body, head, leftArmPivot, rightArmPivot, leftLeg, rightLeg };
    record.mouth = mouth;
  }

  createBystanders() {
    const definitions = [
      { key: 'bystander-a', name: 'Bystander', color: '#59626f', skincolor: '#6f4834', position: [-3.7, 0, -5.4] },
      { key: 'bystander-b', name: 'Bystander', color: '#756651', skincolor: '#9a6547', position: [3.4, 0, -6.1] },
      { key: 'bystander-c', name: 'Bystander', color: '#434856', skincolor: '#744d39', position: [4.8, 0, -7.8] }
    ];
    definitions.forEach(definition => {
      const root = new pc.Entity(definition.key);
      root.setPosition(definition.position[0], definition.position[1], definition.position[2]);
      root.lookAt(0, 1.4, -3.5);
      root.rotateLocal(0, 180, 0);
      root._experiencePhase = Math.random() * Math.PI * 2;
      this.app.root.addChild(root);
      const record = { root, definition, renderMode: 'procedural_3d', animationNames: [], mouthTargets: [] };
      this.createProceduralCharacter(record, true);
      const phone = primitive('phone', 'box', material('#080b10', { emissive: '#284a75' }), [0.12, 0.22, 0.035], [0.33, 1.7, -0.2]);
      root.addChild(phone);
      record.phone = phone;
      this.bystanders.push(record);
    });
  }

  bindControls() {
    this.canvas.addEventListener('pointerdown', event => {
      this.drag = { x: event.clientX, y: event.clientY, yaw: this.lookYaw, pitch: this.lookPitch };
      this.canvas.setPointerCapture?.(event.pointerId);
    });
    this.canvas.addEventListener('pointermove', event => {
      if (!this.drag || this.sensor.active || this.mode === MODE_XR) return;
      this.manualLook = true;
      this.lookYaw = this.drag.yaw - (event.clientX - this.drag.x) * 0.16;
      this.lookPitch = Math.max(-72, Math.min(72, this.drag.pitch - (event.clientY - this.drag.y) * 0.14));
    });
    const release = () => { this.drag = null; };
    this.canvas.addEventListener('pointerup', release);
    // Phone headset: a tap on the screen (or a viewer's trigger button) picks the choice being looked at.
    this.canvas.addEventListener('click', () => this.remoteSelect());
    // Bluetooth viewer remotes connect as a keyboard, a mouse or a game controller: any of them picks the
    // choice being looked at. (Remotes in volume/music-key mode cannot be seen by a web page.)
    window.addEventListener('keydown', event => {
      if (['Enter', 'NumpadEnter', ' ', 'Spacebar', 'MediaPlayPause', 'Select'].includes(event.key)) {
        if (this.remoteSelect()) event.preventDefault();
      }
    });
    document.addEventListener('click', event => { if (event.target !== this.canvas && this.mode === MODE_HEADSET) this.remoteSelect(); });
    this.padPressed = false;
    this.canvas.addEventListener('pointercancel', release);
  }

  async setMode(mode) {
    this.mode = mode;
    this.disableStereo();
    if (mode !== MODE_SPATIAL && mode !== MODE_HEADSET) this.sensor.stop();
    if (mode === MODE_SPATIAL || mode === MODE_HEADSET) {
      const granted = await this.sensor.request();
      if (!granted) {
        this.mode = MODE_BROWSER;
        this.callbacks.sensorDenied?.();
        return MODE_BROWSER;
      }
      if (mode === MODE_HEADSET) {
        this.enableStereo();
        await this.lockLandscape();
      }
    }
    if (mode === MODE_XR) {
      const entered = await this.enterXr();
      if (!entered) {
        this.mode = MODE_BROWSER;
        this.callbacks.xrUnavailable?.();
        return MODE_BROWSER;
      }
    }
    return this.mode;
  }

  enableStereo() {
    // Clone BEFORE disabling the main camera: a clone copies the disabled state, which left
    // phone-headset mode rendering nothing (the screen kept its last frame).
    this.stereoLeft = this.camera.clone();
    this.stereoRight = this.camera.clone();
    this.stereoLeft.camera.enabled = true;
    this.stereoRight.camera.enabled = true;
    this.camera.camera.enabled = false;
    this.stereoLeft.name = 'camera-left';
    this.stereoRight.name = 'camera-right';
    this.stereoLeft.camera.rect = new pc.Vec4(0, 0, 0.5, 1);
    this.stereoRight.camera.rect = new pc.Vec4(0.5, 0, 0.5, 1);
    this.stereoLeft.setLocalPosition(-0.032, 0, 0);
    this.stereoRight.setLocalPosition(0.032, 0, 0);
    this.cameraRig.addChild(this.stereoLeft);
    this.cameraRig.addChild(this.stereoRight);
    this.createReticle();
    this.reticle.enabled = true;
    this.headsetAngle = undefined;
    document.body.classList.add('headset-mode');
  }

  // Android (Chrome): go fullscreen and lock landscape so the phone's rotation lock doesn't matter.
  // iPhone can't lock; updateHeadsetLayout() rotates the view itself instead.
  async lockLandscape() {
    try {
      if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
        this.enteredFullscreen = true;
      }
      await screen.orientation?.lock?.('landscape');
    } catch {
      // Not supported here; the portrait fallback handles it.
    }
  }

  // Keep the split screen side by side however the page is oriented. If the page stays in
  // portrait while the phone is held sideways, rotate the whole view to match the phone.
  updateHeadsetLayout() {
    if (this.mode !== MODE_HEADSET && this.mode !== MODE_SPATIAL) return;
    let angle = null;
    if (innerHeight > innerWidth) {
      const best = this.sensor.bestScreenAngle();
      angle = best && best.y > 0.55 ? best.angle : (this.headsetAngle ?? 0);
    }
    if (angle === this.headsetAngle) return;
    this.headsetAngle = angle;
    this.sensor.angleOverride = angle;
    const classes = document.body.classList;
    classes.toggle('headset-rot-90', angle === 90);
    classes.toggle('headset-rot-270', angle === 270);
    // only the split-screen viewer needs the phone sideways; "use your phone" works upright too
    classes.toggle('headset-turn', angle === 0 && this.mode === MODE_HEADSET);
    this.applyCanvasSize();
  }

  applyCanvasSize() {
    const rotated = (this.mode === MODE_HEADSET || this.mode === MODE_SPATIAL) && (this.headsetAngle === 90 || this.headsetAngle === 270);
    const width = rotated ? innerHeight : innerWidth;
    const height = rotated ? innerWidth : innerHeight;
    document.documentElement.style.setProperty('--app-w', `${width}px`);
    document.documentElement.style.setProperty('--app-h', `${height}px`);
    if (rotated) {
      this.app.setCanvasFillMode(pc.FILLMODE_NONE, width, height);
      this.app.resizeCanvas(width, height);
    } else {
      this.app.setCanvasFillMode(pc.FILLMODE_FILL_WINDOW);
      this.app.resizeCanvas();
    }
  }

  // Spoken line / outcome text inside the 3D scene for phone-headset and VR, where the page panel is hidden.
  setCaption(text) {
    if (this.captionEntity) {
      this.captionEntity.destroy();
      this.captionEntity = null;
    }
    if (!text || (this.mode !== MODE_XR && this.mode !== MODE_HEADSET)) return;
    const eye = this.viewerEye();
    const entity = this.createTextPlane(text, 1.6, 0.3, '#0b1426');
    entity.setPosition(eye.x, eye.y - 0.1, eye.z - 1.68);
    entity.lookAt(eye);
    entity.rotateLocal(BOARD_TILT_X, BOARD_SPIN_Y, 0);
    this.app.root.addChild(entity);
    this.captionEntity = entity;
  }

  // Center dot for gaze selection; grows and turns gold while a choice is being held.
  createReticle() {
    if (this.reticle) return;
    const mat = new pc.StandardMaterial();
    mat.diffuse = new pc.Color(0, 0, 0);
    mat.emissive = new pc.Color(1, 1, 1);
    mat.useLighting = false;
    mat.depthTest = false;
    mat.update();
    this.reticleMaterial = mat;
    this.reticle = new pc.Entity('gaze-reticle');
    this.reticle.addComponent('render', { type: 'sphere', material: mat, castShadows: false, receiveShadows: false });
    this.reticle.setLocalPosition(0, 0, -1.2);
    this.reticle.setLocalScale(0.018, 0.018, 0.018);
    this.cameraRig.addChild(this.reticle);
  }

  setReticleProgress(progress) {
    if (!this.reticle) return;
    const size = 0.018 + progress * 0.03;
    this.reticle.setLocalScale(size, size, size);
    this.reticleMaterial.emissive = progress > 0 ? new pc.Color(1, 0.78 - progress * 0.1, 0.3) : new pc.Color(1, 1, 1);
    this.reticleMaterial.update();
  }

  disableStereo() {
    document.body.classList.remove('headset-mode', 'headset-rot-90', 'headset-rot-270', 'headset-turn');
    if (this.reticle) this.reticle.enabled = false;
    if (this.sensor) this.sensor.angleOverride = null;
    const wasRotated = this.headsetAngle === 90 || this.headsetAngle === 270;
    this.headsetAngle = undefined;
    if (wasRotated) this.applyCanvasSize();
    try { screen.orientation?.unlock?.(); } catch { /* not supported */ }
    if (this.enteredFullscreen && document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    this.enteredFullscreen = false;
    if (this.stereoLeft) this.stereoLeft.destroy();
    if (this.stereoRight) this.stereoRight.destroy();
    this.stereoLeft = null;
    this.stereoRight = null;
    if (this.camera?.camera) this.camera.camera.enabled = true;
  }

  async enterXr() {
    if (!this.app.xr.supported || !this.app.xr.isAvailable(pc.XRTYPE_VR)) return false;
    return new Promise(resolve => {
      this.app.xr.start(this.camera.camera, pc.XRTYPE_VR, pc.XRSPACE_LOCALFLOOR, error => {
        if (error) {
          resolve(false);
          return;
        }
        // local-floor poses already include the wearer's head height; keep the rig on the floor
        // or the viewer floats ~1.6 m above the scene, the screen and the choice boards.
        const rig = this.cameraRig.getPosition();
        this.rigHeightBeforeXr = rig.y;
        this.cameraRig.setPosition(rig.x, 0, rig.z);
        this.cameraRig.setEulerAngles(0, 0, 0);
        this.app.xr.once('end', () => {
          const current = this.cameraRig.getPosition();
          this.cameraRig.setPosition(current.x, this.rigHeightBeforeXr ?? 1.66, current.z);
          this.setCaption('');
          this.clearChoiceBoards();
          this.mode = MODE_BROWSER;
          this.callbacks.xrEnded?.();
        });
        resolve(true);
      });
    });
  }

  attachXrInput(inputSource) {
    if (this.xrInputAttached.has(inputSource)) return;
    this.xrInputAttached.add(inputSource);
    inputSource.on('select', () => this.selectFromRay(inputSource.getOrigin(), inputSource.getDirection()));
  }

  boardHitByRay(origin, direction) {
    const ray = new pc.Ray(origin, direction);
    let selected = null;
    let distance = Number.POSITIVE_INFINITY;
    for (const board of this.choiceBoards) {
      board.bounds.center.copy(board.entity.getPosition());
      const hit = new pc.Vec3();
      if (board.bounds.intersectsRay(ray, hit)) {
        const current = hit.distance(origin);
        if (current < distance) {
          distance = current;
          selected = { board, distance };
        }
      }
    }
    return selected;
  }

  updateXrPointers() {
    if (this.mode !== MODE_XR || !this.app.xr.active) return;
    let hovered = null;
    for (const source of this.app.xr.input.inputSources) {
      const origin = source.getOrigin();
      const direction = source.getDirection();
      if (!origin || !direction) continue;
      const hit = this.boardHitByRay(origin, direction);
      const length = hit ? hit.distance : 3;
      const end = new pc.Vec3().copy(direction).mulScalar(length).add(origin);
      this.app.drawLine(origin, end, hit ? XR_RAY_HOT : XR_RAY_COLOR);
      if (hit) hovered = hit.board;
    }
    for (const board of this.choiceBoards) {
      if (!board.baseScale) board.baseScale = board.entity.getLocalScale().clone();
      const grow = board === hovered ? 1.08 : 1;
      board.entity.setLocalScale(board.baseScale.x * grow, board.baseScale.y, board.baseScale.z * grow);
    }
  }

  selectFromRay(origin, direction) {
    const ray = new pc.Ray(origin, direction);
    let selected = null;
    let distance = Number.POSITIVE_INFINITY;
    for (const board of this.choiceBoards) {
      board.bounds.center.copy(board.entity.getPosition());
      const hit = new pc.Vec3();
      if (board.bounds.intersectsRay(ray, hit)) {
        const current = hit.distance(origin);
        if (current < distance) {
          distance = current;
          selected = board;
        }
      }
    }
    if (selected) this.callbacks.choose?.(selected.choice);
  }

  async playPerformanceVideo(url) {
    this.stopPerformanceVideo();
    if (!url) return false;
    const video = document.createElement('video');
    video.crossOrigin = 'anonymous';
    video.playsInline = true;
    video.muted = true;
    // Play the clip once, then keep its final seconds cycling instead of restarting the whole action.
    video.loop = false;
    video.addEventListener('ended', () => {
      if (!video.getAttribute('src')) return;
      const length = video.duration;
      if (!Number.isFinite(length) || length <= 0) return;
      video.currentTime = Math.max(0, length - 2.5);
      video.play().catch(() => {});
    });
    video.preload = 'auto';
    video.src = url;
    const loaded = await new Promise(resolve => {
      let settled = false;
      const finish = value => {
        if (settled) return;
        settled = true;
        resolve(value);
      };
      video.addEventListener('loadeddata', () => finish(true), { once: true });
      video.addEventListener('error', () => finish(false), { once: true });
      setTimeout(() => finish(false), 10000);
      video.load();
    });
    if (!loaded) {
      video.removeAttribute('src');
      video.load();
      return false;
    }
    const texture = new pc.Texture(this.app.graphicsDevice, {
      format: pc.PIXELFORMAT_RGBA8,
      mipmaps: false
    });
    texture.minFilter = pc.FILTER_LINEAR;
    texture.magFilter = pc.FILTER_LINEAR;
    texture.addressU = pc.ADDRESS_CLAMP_TO_EDGE;
    texture.addressV = pc.ADDRESS_CLAMP_TO_EDGE;
    texture.setSource(video);
    const performanceMaterial = new pc.StandardMaterial();
    performanceMaterial.emissive = pc.Color.WHITE;
    performanceMaterial.emissiveMap = texture;
    performanceMaterial.diffuse = pc.Color.BLACK;
    performanceMaterial.useLighting = false;
    performanceMaterial.cull = pc.CULLFACE_NONE;
    performanceMaterial.update();
    const aspect = video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : 16 / 9;
    // Curved screen that wraps around the viewer: the clip fills the view when looking ahead,
    // and turning the phone / head pans across the scene (Malik on the left, Dre on the right).
    const entity = this.buildCurvedScreen(texture, aspect, performanceMaterial);
    this.app.root.addChild(entity);
    this.performanceVideo = video;
    this.performanceTexture = texture;
    this.performanceMaterial = performanceMaterial;
    this.performanceEntity = entity;
    try {
      await video.play();
      return true;
    } catch (error) {
      this.stopPerformanceVideo();
      throw error;
    }
  }

  // Where the viewer's eyes are. In XR the headset pose supplies it; elsewhere the rig is the eye.
  viewerEye() {
    if (this.mode === MODE_XR) {
      const head = this.camera.getPosition();
      const rig = this.cameraRig.getPosition();
      if (head.y - rig.y > 0.8) return head.clone();
      return new pc.Vec3(rig.x, rig.y + 1.6, rig.z);
    }
    return this.cameraRig.getPosition().clone();
  }

  buildCurvedScreen(texture, aspect, baseMaterial) {
    const slices = 24;
    const radius = 2.6;
    const arc = 100 * pc.math.DEG_TO_RAD;
    const height = (radius * arc) / aspect;
    const sliceWidth = 2 * radius * Math.sin(arc / (2 * slices)) * 1.002;
    const center = this.viewerEye();
    const root = new pc.Entity('approved-character-performance');
    root.setPosition(center.x, center.y - 0.1, center.z);
    this.performanceSliceMaterials = [];
    for (let index = 0; index < slices; index += 1) {
      const angle = -arc / 2 + (index + 0.5) * (arc / slices);
      const sliceMaterial = index === 0 ? baseMaterial : baseMaterial.clone();
      sliceMaterial.emissiveMap = texture;
      sliceMaterial.emissiveMapTiling = new pc.Vec2(1 / slices, 1);
      sliceMaterial.emissiveMapOffset = new pc.Vec2(index / slices, 0);
      sliceMaterial.update();
      if (index > 0) this.performanceSliceMaterials.push(sliceMaterial);
      const pivot = new pc.Entity(`screen-slice-${index}`);
      pivot.setLocalEulerAngles(0, -angle * pc.math.RAD_TO_DEG, 0);
      const slice = primitive(`screen-slice-plane-${index}`, 'plane', sliceMaterial, [sliceWidth, 1, height], [0, 0, -radius * Math.cos(arc / (2 * slices))]);
      slice.setLocalEulerAngles(90, 0, 0);
      slice.render.castShadows = false;
      pivot.addChild(slice);
      root.addChild(pivot);
    }
    return root;
  }

  stopPerformanceVideo() {
    if (this.performanceVideo) {
      this.performanceVideo.pause();
      this.performanceVideo.removeAttribute('src');
      this.performanceVideo.load();
    }
    this.performanceEntity?.destroy();
    (this.performanceSliceMaterials || []).forEach(item => item.destroy());
    this.performanceSliceMaterials = [];
    this.performanceMaterial?.destroy();
    this.performanceTexture?.destroy();
    this.performanceVideo = null;
    this.performanceTexture = null;
    this.performanceMaterial = null;
    this.performanceEntity = null;
  }

  setChoices(choices) {
    this.clearChoiceBoards();
    this.gaze = null;
    this.setReticleProgress(0);
    if (this.mode !== MODE_XR && this.mode !== MODE_HEADSET) return;
    const eye = this.viewerEye();
    choices.forEach((choice, index) => {
      const width = 1.5;
      const height = 0.27;
      const entity = this.createTextPlane(choice.label, width, height, '#15243d');
      // Stacked just below eye level, in front of the curved screen.
      entity.setPosition(eye.x, eye.y - 0.42 - index * 0.32, eye.z - 1.7);
      entity.lookAt(eye);
      // A plane primitive faces +Y; turn that face toward the viewer (the old 0/180/0 left it edge-on).
      entity.rotateLocal(BOARD_TILT_X, BOARD_SPIN_Y, 0);
      this.app.root.addChild(entity);
      this.choiceBoards.push({
        entity,
        choice,
        bounds: new pc.BoundingBox(entity.getPosition().clone(), new pc.Vec3(width / 2, height / 2, 0.14))
      });
    });
  }

  // A remote, a key or a tap picks the choice under the centre dot (phone headset) right away.
  remoteSelect() {
    if (this.mode !== MODE_HEADSET && this.mode !== MODE_SPATIAL) return false;
    if (!this.gaze) return false;
    this.gaze.time = GAZE_SECONDS;
    return true;
  }

  pollGamepads() {
    const pads = navigator.getGamepads ? [...navigator.getGamepads()].filter(Boolean) : [];
    const pressed = pads.some(pad => pad.buttons.some(button => button.pressed));
    if (pressed && !this.padPressed) this.remoteSelect();
    this.padPressed = pressed;
  }

  // Phone-headset mode: look at a choice for GAZE_SECONDS to pick it (or tap the screen).
  updateGaze(delta) {
    if (this.mode !== MODE_HEADSET || !this.choiceBoards.length) return;
    const origin = this.cameraRig.getPosition();
    const ray = new pc.Ray(origin, this.cameraRig.forward.clone());
    let target = null;
    for (const board of this.choiceBoards) {
      board.bounds.center.copy(board.entity.getPosition());
      if (board.bounds.intersectsRay(ray, new pc.Vec3())) target = board;
    }
    if (!target) {
      if (this.gaze) this.gaze.board.entity.setLocalScale(this.gaze.scale);
      this.gaze = null;
      this.setReticleProgress(0);
      return;
    }
    if (!this.gaze || this.gaze.board !== target) {
      if (this.gaze) this.gaze.board.entity.setLocalScale(this.gaze.scale);
      this.gaze = { board: target, time: 0, scale: target.entity.getLocalScale().clone() };
    }
    this.gaze.time += delta;
    const progress = Math.min(1, this.gaze.time / GAZE_SECONDS);
    this.setReticleProgress(progress);
    const grow = 1 + progress * 0.12;
    target.entity.setLocalScale(this.gaze.scale.x * grow, this.gaze.scale.y, this.gaze.scale.z * grow);
    if (this.gaze.time >= GAZE_SECONDS) {
      const choice = target.choice;
      this.gaze = null;
      this.setReticleProgress(0);
      this.callbacks.choose?.(choice);
    }
  }

  createTextPlane(text, width, height, background) {
    const canvas = document.createElement('canvas');
    canvas.width = 2048;
    canvas.height = 440;
    const context = canvas.getContext('2d');
    context.fillStyle = background;
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.strokeStyle = '#67e0c0';
    context.lineWidth = 12;
    context.strokeRect(6, 6, canvas.width - 12, canvas.height - 12);
    context.fillStyle = '#ffffff';
    context.font = '700 88px Arial';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    const words = String(text).split(' ');
    const lines = [];
    let line = '';
    words.forEach(word => {
      const candidate = `${line}${word} `;
      if (context.measureText(candidate).width > 1840 && line) {
        lines.push(line.trim());
        line = `${word} `;
      } else {
        line = candidate;
      }
    });
    lines.push(line.trim());
    const firstY = canvas.height / 2 - ((lines.length - 1) * 98) / 2;
    lines.forEach((item, index) => context.fillText(item, canvas.width / 2, firstY + index * 98));
    const texture = new pc.Texture(this.app.graphicsDevice, { width: canvas.width, height: canvas.height, format: pc.PIXELFORMAT_RGBA8, mipmaps: true });
    texture.setSource(canvas);
    texture.minFilter = pc.FILTER_LINEAR_MIPMAP_LINEAR;
    texture.magFilter = pc.FILTER_LINEAR;
    texture.anisotropy = 8;
    const planeMaterial = new pc.StandardMaterial();
    planeMaterial.emissive = pc.Color.WHITE;
    planeMaterial.emissiveMap = texture;
    planeMaterial.diffuse = pc.Color.BLACK;
    planeMaterial.useLighting = false;
    planeMaterial.cull = pc.CULLFACE_NONE;
    planeMaterial.update();
    return primitive('xr-choice', 'plane', planeMaterial, [width, 1, height]);
  }

  clearChoiceBoards() {
    this.choiceBoards.forEach(board => board.entity.destroy());
    this.choiceBoards = [];
  }

  stage(node) {
    this.focusKey = node.focus || '';
    if (this.stagedMode) {
      this.manualLook = false;
      this.staged.stage(node);
      return;
    }
    const staged = node.stage || {};
    for (const [key, record] of this.characters.entries()) {
      const target = staged[key] || record.definition.position || [0, 0, -4];
      record.root._experienceTarget.copy(readablePosition(target, [0, 0, -4]));
      record.root._experienceAction = key === this.focusKey ? (node.animation || 'talk') : 'idle';
      this.playAction(record, record.root._experienceAction);
    }
  }

  // Seconds until the cast reaches their places for this moment (staged scene only).
  stagingDelay() { return this.stagedMode ? this.staged.walkTime() : 0; }

  setSpeaking(key) { this.speakingKey = key || ''; this.speakingLevels = null; }

  // Live scenes: several people can talk at once; levels is { key: mouthLevel } (empty object = nobody).
  setSpeakingLevels(levels) { this.speakingLevels = levels; }

  setPrototypeVisibility(visible) {
    if (this.stagedMode) return;
    for (const record of this.characters.values()) {
      if (record.renderMode === 'procedural_3d') record.root.enabled = visible;
    }
    this.bystanders.forEach(record => { record.root.enabled = visible; });
  }

  playAction(record, requested) {
    if (record.renderMode !== 'glb' || !record.model?.animation) return;
    const candidates = record.animationNames;
    const lowered = requested.toLowerCase();
    const name = candidates.find(item => item.toLowerCase() === lowered)
      || candidates.find(item => item.toLowerCase().includes(lowered))
      || candidates.find(item => item.toLowerCase().includes(requested === 'talk' ? 'talk' : 'idle'))
      || candidates[0];
    if (name) record.model.animation.play(name, 0.25);
  }

  setMouthLevel(level) {
    this.mouthLevel = Math.max(0, Math.min(1, Number(level || 0)));
  }

  recenter() {
    this.sensor.recenter();
    this.lookYaw = 0;
    this.lookPitch = -4;
  }

  update(delta) {
    this.elapsed += delta;
    if (this.performanceTexture && this.performanceVideo?.readyState >= 2) this.performanceTexture.upload();
    if (this.mode === MODE_SPATIAL || this.mode === MODE_HEADSET) {
      this.updateHeadsetLayout();
      this.sensor.update(this.cameraRig);
      this.updateGaze(delta);
      this.pollGamepads();
    } else if (this.mode !== MODE_XR) {
      if (this.stagedMode && !this.manualLook) {
        // browser: frame the speaker and whoever they face; drag to look around takes over until the next moment
        // introShot: the scene intro asks for a close shot of the person being named (2026-10-13)
        const want = this.staged.frame(this.introShot || 'auto', this.canvas.clientWidth / Math.max(1, this.canvas.clientHeight));
        // the decision panel covers the lower part of the screen: aim lower so people sit in the open upper part
        if (!this.introShot && !document.body.classList.contains('panel-min')) want.pitch -= want.fov * 0.2;
        const k = Math.min(1, delta * 3.2);
        let dy = (want.yaw - this.lookYaw) % 360; if (dy > 180) dy -= 360; if (dy < -180) dy += 360;
        this.lookYaw += dy * k;
        this.lookPitch += (want.pitch - this.lookPitch) * k;
        this.camera.camera.fov += (want.fov - this.camera.camera.fov) * k;
      }
      this.cameraRig.setEulerAngles(this.lookPitch, this.lookYaw, 0);
    } else {
      this.updateXrPointers();
    }
    if (this.stagedMode) {
      if (this.speakingLevels) this.staged.update(delta, this.speakingLevels);
      else this.staged.update(delta, this.speakingKey, this.speakingKey ? this.mouthLevel : 0);
      return;
    }
    for (const [key, record] of this.characters.entries()) {
      const current = record.root.getPosition();
      const target = record.root._experienceTarget;
      record.root.setPosition(
        pc.math.lerp(current.x, target.x, Math.min(1, delta * 4.5)),
        pc.math.lerp(current.y, target.y, Math.min(1, delta * 4.5)),
        pc.math.lerp(current.z, target.z, Math.min(1, delta * 4.5))
      );
      if (record.renderMode === 'procedural_3d') this.animateProcedural(record, delta, key === this.focusKey);
      if (record.mouthTargets.length) {
        const weight = key === this.focusKey ? this.mouthLevel : 0;
        record.mouthTargets.forEach(targetItem => targetItem.morphInstance.setWeight(targetItem.index, weight));
      }
    }
    this.bystanders.forEach((record, index) => {
      const pulse = Math.sin(this.elapsed * (0.7 + index * 0.12) + record.root._experiencePhase);
      record.root.setEulerAngles(0, pulse * 4 + (index % 2 ? -18 : 18), 0);
      record.parts.rightArmPivot.setLocalEulerAngles(38 + pulse * 3, 0, -24);
      record.phone.setLocalEulerAngles(8, pulse * 4, 0);
    });
  }

  animateProcedural(record, delta, focused) {
    const phase = this.elapsed * (focused ? 3.1 : 1.35) + record.root._experiencePhase;
    const talk = focused && record.root._experienceAction.includes('talk');
    const gesture = Math.sin(phase) * (talk ? 22 : 3);
    record.parts.leftArmPivot.setLocalEulerAngles(talk ? -12 : 0, 0, 8 + gesture);
    record.parts.rightArmPivot.setLocalEulerAngles(talk ? 16 : 0, 0, -8 - gesture * 0.75);
    record.parts.head.setLocalEulerAngles(Math.sin(phase * 0.47) * 2.2, Math.sin(phase * 0.35) * 5, 0);
    record.parts.body.setLocalPosition(0, 1.22 + Math.sin(phase * 0.55) * 0.012, 0);
    if (record.mouth) record.mouth.setLocalScale(0.18, 0.025 + (focused ? this.mouthLevel * 0.12 : 0), 0.025);
    void delta;
  }
}

export const ViewModes = Object.freeze({
  BROWSER: MODE_BROWSER,
  SPATIAL: MODE_SPATIAL,
  HEADSET: MODE_HEADSET,
  XR: MODE_XR
});
